import React, { FC } from 'react';
import { Element } from 'react-scroll';
import AddButton from '@components/profile/common/addButton';
import useProfileBasic from '@repository/profiles/useProfileBasic';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { useBasicInfo } from '@repository/users/useUsers';
import ProfileBasicVO from '@domain/vo/profile/ProfileBasicVO';
import { ProfileInfo } from '@domain/constant/profile/ProfileOverlayType';

import TextFormat from '@utils/text/format';
import DateFormat from '@utils/date/format';
import classnames from 'classnames/bind';
import styles from './basicInfo.module.scss';

const cx = classnames.bind(styles);

interface IBasicInfoDefault {
  onOpenOverlay: () => void;
}

// 정보 입력 전 기본 페이지
const DefaultPage: FC<IBasicInfoDefault> = ({ onOpenOverlay }) => {
  const { data, isLoading } = useBasicInfo();
  const name = isLoading ? '' : data?.name || '김조이';
  const birthDate = isLoading ? '' : data?.birthDate || '2021.08.15';
  const mobile = isLoading ? '' : data?.mobile || '010-0000-0000';
  const email = isLoading ? '' : data?.email || 'supports@midasin.ai';

  return (
    <Element name={ProfileInfo.BASIC_INFO} className={cx('infoArea')}>
      <div className={cx('infoInner')}>
        <span className={cx('profileImage', 'default')} />
        <div className={cx('infoWrap')}>
          {name && (
            <div className={cx('nameArea')}>
              <em className={cx('name')}>{name}</em>
              <span className={cx('require')}>필수</span>
            </div>
          )}
          <div className={cx('subInfoArea')}>
            {birthDate && (
              <span className={cx('subInfo')}>{DateFormat.getDateFormatString(birthDate, 'yyyy.MM.dd')}</span>
            )}
            {mobile && <span className={cx('subInfo')}>{TextFormat.formatMobileWithHypen(mobile)}</span>}
            {email && <span className={cx('subInfo')}>{email}</span>}
          </div>
        </div>
      </div>

      <AddButton onClick={onOpenOverlay} />
    </Element>
  );
};

interface IBasicContentsProps {
  profileBasic: ProfileBasicVO;
}

const BasicContents: FC<IBasicContentsProps> = ({ profileBasic }) => {
  const { name, profileImageUrl, email, birthDateText, mobileText, isPeregrine } = profileBasic;
  const profileImageStyle = profileImageUrl ? { backgroundImage: `url(${profileImageUrl})` } : {};

  return (
    <div className={cx('infoInner')}>
      <span className={cx('profileImage', { default: !profileImageUrl })} style={profileImageStyle} />
      <div className={cx('infoWrap')}>
        <div className={cx('nameArea')}>
          {/* 이름 */}
          <em className={cx('name')}>{name}</em>
          {/* 취준 상태 */}
          <span className={cx('require')}>필수</span>
        </div>
        <div className={cx('subInfoArea')}>
          {/* 생년월일 */}
          {birthDateText && <span className={cx('subInfo')}>{birthDateText}</span>}
          {/* 휴대폰 */}
          {mobileText && <span className={cx('subInfo')}>{isPeregrine ? '해외거주자' : mobileText}</span>}
          {/* 이메일 */}
          {email && <span className={cx('subInfo')}>{email}</span>}
        </div>
      </div>
    </div>
  );
};

interface IBasicInfoProps {
  onOpenOverlay: () => void;
}

const BasicInfo: FC<IBasicInfoProps> = ({ onOpenOverlay }) => {
  const { data: userInfo } = useUserInfo();
  const { data: profileBasic } = useProfileBasic(!!userInfo?.id);

  if (!userInfo?.id || !profileBasic?.saveYn) return <DefaultPage onOpenOverlay={onOpenOverlay} />;

  return (
    <Element name={ProfileInfo.BASIC_INFO} className={cx('infoArea')}>
      <BasicContents profileBasic={profileBasic} />

      <AddButton onClick={onOpenOverlay} />
    </Element>
  );
};

export default BasicInfo;
